import { Outlet, useLocation } from '@tanstack/react-router';
import StandardHeader from './Header.tsx';
import StandardFooter from './Footer.tsx';
import WelcomeBackModal from '../WelcomeBackModal';
import UpdateModal from './UpdateModal';
import CookieConsent from './CookieConsent';
import { useUserStatus } from '../../hooks/useUserStatus';
import { useState, useEffect } from 'react';
import './Layout.css';

export default function Layout() {
    const { userStatus } = useUserStatus();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
    const location = useLocation();

    // Force scroll to top on route change (SPA reliability fix)
    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant' as any // Use instant for immediate reset
        });
    }, [location.pathname, location.search]);

    // Listen for PWA update events
    useEffect(() => {
        const handleSwUpdate = (event: CustomEvent<{ worker: ServiceWorker }>) => {
            setWaitingWorker(event.detail.worker);
            setShowUpdateModal(true);
        };

        window.addEventListener('swUpdate', handleSwUpdate as EventListener);
        return () => {
            window.removeEventListener('swUpdate', handleSwUpdate as EventListener);
        };
    }, []);

    // Handle update action
    const handleUpdate = () => {
        if (waitingWorker) {
            // Tell the waiting service worker to activate
            waitingWorker.postMessage({ type: 'SKIP_WAITING' });
            setShowUpdateModal(false);
            // Reload after the new service worker takes control
            window.location.reload();
        }
    };

    // Handle dismiss
    const handleDismissUpdate = () => {
        setShowUpdateModal(false);
    };

    // Check if on trip page or discover page to avoid footer
    const isTripPage = location.pathname.startsWith('/trip/') || 
                       location.pathname.startsWith('/ro/trip/') || 
                       location.pathname.startsWith('/en/trip/');
    const isDiscoverPage = location.pathname === '/discover' || location.pathname === '/ro/discover' || location.pathname === '/en/discover';
    const isAdminPage = location.pathname === '/admin' || location.pathname.startsWith('/admin/') ||
                        location.pathname === '/ro/admin' || location.pathname.startsWith('/ro/admin/') ||
                        location.pathname === '/en/admin' || location.pathname.startsWith('/en/admin/');
    
    const hideFooter = isTripPage || isDiscoverPage || isAdminPage;
    const hideHeader = isAdminPage;

    // Admin page has its own full layout
    if (isAdminPage) {
        return (
            <div className="app-shell admin-shell">
                <main className="main-content full-height">
                    <Outlet />
                </main>
            </div>
        );
    }

    return (
        <div className={`app-shell ${isTripPage ? 'trip-page-shell' : ''}`}>
            {!hideHeader && (
                <StandardHeader
                    userStatus={userStatus}
                    onShowLogin={() => setShowLoginModal(true)}
                />
            )}

            <main className={`main-content ${hideFooter ? 'full-height' : ''}`}>
                <Outlet />
            </main>

            {!hideFooter && <StandardFooter />}

            <WelcomeBackModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                redirectPath={location.pathname}
            />

            <UpdateModal
                isOpen={showUpdateModal}
                onUpdate={handleUpdate}
                onDismiss={handleDismissUpdate}
            />

            <CookieConsent />
        </div>
    );
}
